// Company Selector Component
// Location: admin-ui/src/components/CompanySelector.tsx

import React from 'react';
import {
  Select,
  MenuItem,
  FormControl,
  Box,
  Avatar,
  Typography,
  CircularProgress,
  Chip,
  IconButton
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import { useCompany } from '../contexts/CompanyContext';

const CompanySelector: React.FC = () => {
  const { currentCompany, companies, switchCompany, loading, error } = useCompany();

  if (loading) {
    return <CircularProgress size={24} />;
  }

  if (error) {
    return <Chip label="Error loading companies" color="error" size="small" />;
  }

  if (companies.length === 0) {
    return <Chip label="No companies available" size="small" />;
  }

  // Don't show selector if user only has one company
  if (companies.length === 1) {
    return (
      <Chip
        icon={<BusinessIcon />}
        label={currentCompany?.name}
        variant="outlined"
        size="small"
      />
    );
  }

  return (
    <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
      <Select
        value={currentCompany?.id || ''}
        onChange={(e) => switchCompany(Number(e.target.value))}
        displayEmpty
        sx={{
          backgroundColor: 'background.paper',
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }
        }}
      >
        {companies.map((company) => (
          <MenuItem key={company.id} value={company.id}>
            <Box display="flex" alignItems="center" gap={1} width="100%">
              {company.settings?.logo ? (
                <Avatar 
                  src={company.settings.logo} 
                  sx={{ width: 24, height: 24 }} 
                  variant="square"
                />
              ) : (
                <BusinessIcon fontSize="small" color="action" />
              )}
              <Typography variant="body2" noWrap>
                {company.settings?.companyDisplayName || company.name}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

// Compact version for mobile
export const CompanySelectorCompact: React.FC = () => {
  const { currentCompany, companies, switchCompany, loading } = useCompany();

  if (loading || companies.length <= 1) {
    return null;
  }

  return (
    <IconButton
      onClick={(e) => {
        // Would open a modal or drawer for company selection on mobile
      }}
      size="small"
      color="inherit"
    >
      {currentCompany?.settings?.logo ? (
        <Avatar 
          src={currentCompany.settings.logo} 
          sx={{ width: 28, height: 28 }} 
          variant="square"
        />
      ) : (
        <BusinessIcon />
      )}
    </IconButton>
  );
};

export default CompanySelector;
